import React, { useEffect, useState } from "react";
import Input from "../../FormInputs";
import { useFormik } from "formik";
import {
  franchiseOfferSchema,
  uploadOfferSchema,
} from "../../../../utils/validationSchemas";
import PrimaryButton from "../../PrimaryButton";
import DayTimeSelector from "./DayTimeSelector";
import GoogleMaps from "../../../common/GoogleMaps";
import UploadExcel from "./UploadExcel";
import {
  addFranchise,
  getLocationById,
} from "../../../../services/franchiseService";
import { useDispatch, useSelector } from "react-redux";
import PhoneFormInput from "../../PhoneInput";

const AddNewLocation = ({
  allStores,
  setLocations,
  setSelectedLocations,
  setSelectedLocationIds,
  setLocationNames,
  selectedMerchant,
  onSuccess,
  isDrawer = false
}) => {
  const dispatch = useDispatch();
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const format = "hh:mm A";

  const [fileData, setFileData] = useState();
  const [placeId, setPlaceId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [newLocationId, setNewLocationId] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    franchiseName: "",
    email: "",
    contact: "",
    location: "",
    timeSet: false,
    timeData: days?.map((d) => ({
      day: d,
      openTime: undefined,
      closeTime: undefined,
      isSelected: false,
    })),
  };
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: franchiseOfferSchema,

    onSubmit: async (values) => {
      if (allStores) return;
      setIsSubmitting(true);

      const payload =
        role === "admin"
          ? {
            merchant: selectedMerchant?._id,
            name: values?.franchiseName,
            email: values?.email,
            phone: values?.contact,
            googlePlaceId: placeId !== null ? placeId : null,
            tradingHours: timeData
              .filter((day) => day?.isSelected === true)
              .map((day) => ({
                day: day?.day?.toLowerCase(),
                openTime: day?.openTime ? day?.openTime.format(format) : null,
                closeTime: day?.closeTime
                  ? day?.closeTime.format(format)
                  : null,
              })),
          }
          : {
            name: values?.franchiseName,
            email: values?.email,
            phone: values?.contact,
            googlePlaceId: placeId !== null ? placeId : null,
            tradingHours: timeData
              .filter((day) => day?.isSelected === true)
              .map((day) => ({
                day: day?.day?.toLowerCase(),
                openTime: day?.openTime ? day?.openTime.format(format) : null,
                closeTime: day?.closeTime
                  ? day?.closeTime.format(format)
                  : null,
              })),
          };
      try {
        const addPromise = await dispatch(addFranchise(payload)).unwrap();
        if (addPromise?.success === true) {
          setNewLocationId(addPromise?.data?._id);
          setSelectedLocations((prev) => [addPromise?.data, ...prev]);
          setSelectedLocationIds((prev) => [addPromise?.data?._id, ...prev]);
          setLocationNames((prev) => [addPromise?.data?.name, ...prev]);
          resetForm();
          setPlaceId(null);
          setResetKey(prev => prev + 1); // Reset map
          if (onSuccess) onSuccess();
        }
      } catch (error) {
        console.error("Error creating location:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const getLocationData = async () => {
    const getPromise = await dispatch(
      getLocationById({ id: newLocationId })
    )?.unwrap();
    if (getPromise?.data) {
      setLocations((prev) => [getPromise?.data, ...prev]);
    }
  };

  useEffect(() => {
    if (newLocationId !== null && !allStores) {
      getLocationData();
    }
  }, [newLocationId, allStores]);

  const { franchiseName, email, contact, location, timeSet, timeData } =
    formik.values;

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldError,
    handleSubmit,
    setValues,
    setErrors,
    resetForm,
  } = formik;

  const handleTimeChange = (dayIndex, field, time) => {
    const updatedTimeData = [...timeData];
    updatedTimeData[dayIndex][field] = time;
    setFieldValue("timeData", updatedTimeData);
  };

  useEffect(() => {
    const isAnyDaySelected = timeData?.some((day) => day.isSelected === true);
    // timeSet value is true if NO day is selected (invalid)
    // and false if AT LEAST ONE day is selected (valid)
    const nextTimeSet = !isAnyDaySelected;

    if (formik.values.timeSet !== nextTimeSet) {
      setFieldValue("timeSet", nextTimeSet, true);
    }

    // If they deselect all days after having it selected, make sure it's touched
    if (!isAnyDaySelected && touched.timeData) {
      formik.setFieldTouched("timeSet", true, false);
    }
  }, [timeData, formik.values.timeSet, setFieldValue, touched.timeData]);

  const handleLocationSelect = (selectedLocation) => {
    if (!selectedLocation) {
      setFieldValue("location", "", true);
      setPlaceId(null);
      formik.setFieldTouched("location", true);
      return;
    }
    setFieldValue("location", selectedLocation?.placeName, true);
    setPlaceId(selectedLocation?.placeId);
    setShowMap(false);
    formik.setFieldError("location", null);
  };

  const handleLocationClick = () => {
    setShowMap(true);
  };

  const handleCancel = () => {
    formik.resetForm();
    setPlaceId(null);
    setResetKey(prev => prev + 1); // Reset map
    if (onSuccess) onSuccess();
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched so validation errors appear
    formik.setTouched({
      franchiseName: true,
      email: true,
      contact: true,
      location: true,
      timeSet: true,
    });

    const formErrors = await formik.validateForm();

    if (!location || !placeId) {
      formik.setFieldError("location", "Please select a location from the suggestions.");
      return;
    }

    if (Object.keys(formErrors).length === 0) {
      formik.handleSubmit(e);
    }
  };

  return (
    <div className={`w-full ${isDrawer ? 'drawer-content-wrapper' : ''}`}>
      <div className="w-full mt-[-20px]">
        <h3 className="section-title !mb-1">Store Information<span>*</span></h3>
        <Input
          type="text"
          label=""
          placeholder="Enter store name"
          width="100%"
          radius="5px"
          InputDivClass="w-[100%] mb-4 -mt-4"
          name="franchiseName"
          value={franchiseName}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.franchiseName}
          touched={touched.franchiseName}
        />
        <Input
          type="text"
          label=""
          placeholder="Enter email"
          width="100%"
          radius="5px"
          InputDivClass="w-[100%] mb-4"
          name="email"
          value={email}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.email}
          touched={touched.email}
        />
        <PhoneFormInput
          label={""}
          divClassName="w-full mb-6 relative z-[1005]"
          name="contact"
          value={contact}
          placeholder="Enter contact number"
          handleBlur={handleBlur}
          error={errors.contact}
          touched={touched.contact}
          setFieldValue={setFieldValue}
          inputStyle={{ width: "100%", border: "none" }}
          buttonStyle={{ border: "none" }}
          buttonClass="!rounded-l-[5px] !bg-[#F0F0F0] md:!w-[13%] sm:!w-[15%] lg:!w-[10%] xl:!w-[12%] 2xl:!w-[10%]"
          containerClass="!rounded-[5px] !shadow-none"
          inputClass="lg:!h-[45px] md:!h-[45px] !rounded-[5px] !bg-[#FFFFFF] !text-[#303030] lg:!text-[14px] sm:!text-[14px] !font-[400] !outline-none !font-poppins !pl-[50px] !shadow-none"
          labelClass="hidden"
          errorClass="text-red-600 text-[10px] md:text-[12px] font-[500] ms-1 mt-1 block text-start"
        />

        <div className="mb-6 rounded-[8px] border border-[#E0E0E0] relative z-[1001]">
          <GoogleMaps
            key={resetKey} // Force re-mount to clear internal state on reset
            onLocationSelect={handleLocationSelect}
            onError={(err) => {
              if (err) setFieldError("location", err);
            }}
            searchContainerClass="p-0"
            compactMode={false}
            externalSearchValue={location}
            forceOpenSearch={false}
            placeholder="Enter Business Address"
            options={{ mapTypeControl: false, streetViewControl: false }}
            mapContainerStyle="swoodle-map h-[320px] w-full relative"
            searchBarParentClassName="absolute z-[10] top-3 left-3 right-3"
            searchBarDivClassName="w-full flex items-center h-[45px] relative"
            searchBarInputClassName="navSearchBar h-full w-[100%] ps-4 pe-10 text-[14px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-[5px] border border-[#D9D9D9] placeholder:!text-[#767676] shadow-sm"
            searchIconClassName="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4"
          />
        </div>

        {errors.location && touched.location && (
          <span className="text-red-600 text-[10px] md:text-[12px] font-[500] ms-1 mt-[-20px] mb-6 block text-start font-poppins">
            {errors.location}
          </span>
        )}

        <div className="time-table-card !pt-0">
          <h3 className="section-title -mt-2">Set Your Opening Days & Hours<span>*</span></h3>
          <DayTimeSelector
            timeData={timeData}
            setFieldValue={(name, value, validate) => {
              setFieldValue(name, value, validate);
              if (name.includes("isSelected")) {
                formik.setFieldTouched("timeData", true, false);
              }
            }}
            format={format}
            error={errors?.timeData}
            touched={touched?.timeData}
          />
        </div>

        {errors?.timeSet && touched?.timeSet && (
          <div className="ps-1 mt-2 mb-4">
            <span className="text-red-600 text-[10px] md:text-[12px] font-[500] block text-start font-poppins">
              {errors?.timeSet}
            </span>
          </div>
        )}
      </div>

      {
        isDrawer ? (
          <div className="drawer-footer">
            <span className="cancel-link" onClick={handleCancel}>Cancel</span>
            <button
              type="button"
              className="confirm-btn"
              onClick={handleCustomSubmit}
              disabled={
                isSubmitting ||
                !franchiseName ||
                !email ||
                !contact ||
                !location ||
                !placeId ||
                timeSet
              }
            >
              {isSubmitting ? "Adding..." : "Add"}
            </button>
          </div>
        ) : (
          <div className="flex justify-end mt-8">
            <PrimaryButton
              className="w-[140px] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat"
              label="Submit"
              onClick={handleCustomSubmit}
              disabled={
                isSubmitting ||
                !franchiseName ||
                !email ||
                !contact ||
                !location ||
                !placeId ||
                timeSet
              }
            />
          </div>
        )
      }
    </div >
  );
};

export default AddNewLocation;
